import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { Lock, KeyRound, Fingerprint } from 'lucide-react';
import { motion } from 'framer-motion';
import loginHero from '@/assets/login-hero.jpg';
import { hasPinSetup, hasBiometricSetup } from '@/lib/storage';
import { isBiometricAvailable } from '@/lib/webauthn';

export function LockScreen() {
  const { unlock, unlockBiometric } = useAuth();
  const hasPin = hasPinSetup();
  const hasBio = hasBiometricSetup();
  const [mode, setMode] = useState<'pin' | 'password'>(hasPin ? 'pin' : 'password');
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [bioAvailable, setBioAvailable] = useState(false);

  useEffect(() => {
    if (hasBio) {
      isBiometricAvailable().then(setBioAvailable);
    }
  }, [hasBio]);

  // Auto-prompt biometric on mount
  useEffect(() => {
    if (hasBio && bioAvailable) {
      handleBiometric();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bioAvailable]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value) return;
    setError('');
    setLoading(true);
    try {
      const ok = await unlock(value, mode === 'pin');
      if (!ok) setError(mode === 'pin' ? 'Incorrect PIN' : 'Incorrect password');
    } catch {
      setError('Failed to unlock');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometric = async () => {
    setError('');
    setLoading(true);
    try {
      const ok = await unlockBiometric();
      if (!ok) setError('Biometric verification failed');
    } catch {
      setError('Biometric not available');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(m => m === 'pin' ? 'password' : 'pin');
    setValue('');
    setError('');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 relative overflow-hidden">
      {/* Hero background image — no overlay */}
      <div className="absolute inset-0 z-0">
        <img src={loginHero} alt="" className="h-full w-full object-cover object-top" />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-xs text-center relative z-10 rounded-2xl bg-background/40 backdrop-blur-xl p-4 shadow-lg border border-border/50"
      >
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
          {mode === 'pin' ? <KeyRound className="h-8 w-8 text-primary" /> : <Lock className="h-8 w-8 text-primary" />}
        </div>
        <h1 className="font-display text-2xl font-bold text-foreground">Inception Glen AussiBooks</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {mode === 'pin' ? 'Enter your PIN to unlock' : 'Enter your password to unlock'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type={mode === 'pin' ? 'tel' : 'password'}
            inputMode={mode === 'pin' ? 'numeric' : undefined}
            pattern={mode === 'pin' ? '[0-9]*' : undefined}
            maxLength={mode === 'pin' ? 6 : undefined}
            placeholder={mode === 'pin' ? 'PIN' : 'Password'}
            value={value}
            onChange={e => setValue(mode === 'pin' ? e.target.value.replace(/\D/g, '') : e.target.value)}
            className="h-12 text-center text-lg"
            autoFocus
          />
          {error && (
            <motion.p
              initial={{ x: -10 }}
              animate={{ x: 0 }}
              className="text-sm text-destructive"
            >
              {error}
            </motion.p>
          )}
          <Button type="submit" className="h-12 w-full text-base" disabled={loading}>
            {loading ? 'Unlocking…' : 'Unlock'}
          </Button>
        </form>

        {/* Biometric button */}
        {hasBio && bioAvailable && (
          <Button
            variant="outline"
            className="mt-3 h-12 w-full gap-2 text-base"
            onClick={handleBiometric}
            disabled={loading}
          >
            <Fingerprint className="h-5 w-5" />
            Use Biometric
          </Button>
        )}

        {/* Mode switcher */}
        {hasPin && (
          <button
            onClick={switchMode}
            className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {mode === 'pin' ? 'Use password instead' : 'Use PIN instead'}
          </button>
        )}
      </motion.div>
    </div>
  );
}
